import { createContext, memo, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { isDevOrTestStage } from '~/swr-hooks/utils';

export type WorkspaceAgentComposerLockApi = {
  isComposerLocked: boolean;
  lockComposer: (sourceId: string) => void;
  unlockComposer: (sourceId: string) => void;
};

const defaultComposerLockValue: WorkspaceAgentComposerLockApi = {
  isComposerLocked: false,
  lockComposer: () => undefined,
  unlockComposer: () => undefined,
};

const WorkspaceAgentComposerLockContext = createContext<WorkspaceAgentComposerLockApi>(defaultComposerLockValue);

export const useWorkspaceAgentComposerLock = () => {
  const context = useContext(WorkspaceAgentComposerLockContext);

  if (context === defaultComposerLockValue) {
    const error = new Error('useWorkspaceAgentComposerLock must be used within WorkspaceAgentComposerLockProvider');

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({
      context: 'Workspace agent composer lock context not found',
      error,
    });
  }

  return context;
};

export type WorkspaceAgentComposerLockProviderProps = {
  children: ReactNode;
};

/**
 * The composer and the surfaces that need it quiet are siblings. Each caller holds its own source
 * id, and the composer stays locked until every source has released.
 */
export const WorkspaceAgentComposerLockProvider = memo(({ children }: WorkspaceAgentComposerLockProviderProps) => {
  const [lockSourceIds, setLockSourceIds] = useState<ReadonlySet<string>>(() => new Set());

  const lockComposer = useCallback((sourceId: string) => {
    setLockSourceIds((current) => {
      if (current.has(sourceId)) {
        return current;
      }

      const next = new Set(current);
      next.add(sourceId);
      return next;
    });
  }, []);

  const unlockComposer = useCallback((sourceId: string) => {
    setLockSourceIds((current) => {
      if (!current.has(sourceId)) {
        return current;
      }

      const next = new Set(current);
      next.delete(sourceId);
      return next;
    });
  }, []);

  const contextValue = useMemo(
    () => ({
      isComposerLocked: lockSourceIds.size > 0,
      lockComposer,
      unlockComposer,
    }),
    [lockComposer, lockSourceIds, unlockComposer],
  );

  return (
    <WorkspaceAgentComposerLockContext.Provider value={contextValue}>
      {children}
    </WorkspaceAgentComposerLockContext.Provider>
  );
});

WorkspaceAgentComposerLockProvider.displayName = 'WorkspaceAgentComposerLockProvider';
